import { chipLoad, feedRate, rpmFromSfm, sfmFromRpm } from '../math/cutting';
import { nearestDrill } from '../math/drills';
import { boltHoles, sineBarStack, taperFromDiameters } from '../math/geometry';
import { deg, fixed, trim } from '../math/format';
import { tapDrillInch } from '../math/threads';
import { bilateralLimits, fit, limits } from '../math/tolerance';
import { decimalToFraction, inchToMm } from '../math/units';
import { ShopMathError, type Step } from '../math/types';

export interface ToolInput {
  readonly key: string;
  readonly label: string;
  readonly unit?: string;
  readonly initial: number;
  /** Whole numbers only — hole counts, flute counts. */
  readonly integer?: boolean;
}

export interface ToolOutput {
  readonly label: string;
  readonly value: string;
  /** The one big number. Everything else is supporting detail. */
  readonly primary?: boolean;
}

export interface ToolTable {
  readonly headers: readonly string[];
  readonly rows: readonly (readonly string[])[];
}

export interface ToolResult {
  readonly outputs: readonly ToolOutput[];
  readonly steps: readonly Step[];
  readonly table?: ToolTable;
}

export interface ToolDef {
  readonly id: string;
  readonly name: string;
  readonly blurb: string;
  readonly inputs: readonly ToolInput[];
  readonly compute: (v: Readonly<Record<string, number>>) => ToolResult;
}

const num = (v: Readonly<Record<string, number>>, key: string): number => v[key] ?? 0;

/**
 * Every calculator returns the same Step[] the drill uses for its
 * walk-through. That is the point of Shop Tool Mode: she can reach for a
 * number today and read how it was reached at the same time, from one
 * implementation that is tested once.
 */
export const TOOLS: readonly ToolDef[] = [
  {
    id: 'rpm',
    name: 'Speed — RPM from SFM',
    blurb: 'Cutter diameter and surface speed in, spindle speed out.',
    inputs: [
      { key: 'sfm', label: 'Surface speed', unit: 'SFM', initial: 100 },
      { key: 'diameter', label: 'Cutter diameter', unit: '"', initial: 0.5 },
    ],
    compute: (v) => {
      const solved = rpmFromSfm(num(v, 'sfm'), num(v, 'diameter'));
      const exact = rpmFromSfm(num(v, 'sfm'), num(v, 'diameter'), { exact: true });
      return {
        outputs: [
          { label: 'Spindle speed', value: `${Math.round(solved.value)} RPM`, primary: true },
          { label: 'Using exact 12 ÷ π', value: `${Math.round(exact.value)} RPM` },
        ],
        steps: solved.steps,
      };
    },
  },
  {
    id: 'sfm',
    name: 'Speed — SFM from RPM',
    blurb: 'Checking what surface speed a setup is actually running.',
    inputs: [
      { key: 'rpm', label: 'Spindle speed', unit: 'RPM', initial: 800 },
      { key: 'diameter', label: 'Diameter', unit: '"', initial: 0.5 },
    ],
    compute: (v) => {
      const solved = sfmFromRpm(num(v, 'rpm'), num(v, 'diameter'));
      return {
        outputs: [{ label: 'Surface speed', value: `${fixed(solved.value, 1)} SFM`, primary: true }],
        steps: solved.steps,
      };
    },
  },
  {
    id: 'feed',
    name: 'Feed rate',
    blurb: 'RPM, flutes and chip load into inches per minute.',
    inputs: [
      { key: 'rpm', label: 'Spindle speed', unit: 'RPM', initial: 764 },
      { key: 'flutes', label: 'Flutes', initial: 4, integer: true },
      { key: 'load', label: 'Chip load per tooth', unit: '"', initial: 0.002 },
    ],
    compute: (v) => {
      const solved = feedRate(num(v, 'rpm'), num(v, 'flutes'), num(v, 'load'));
      const back = chipLoad(solved.value, num(v, 'rpm'), num(v, 'flutes'));
      return {
        outputs: [
          { label: 'Feed rate', value: `${fixed(solved.value, 2)} IPM`, primary: true },
          { label: 'Chip load check', value: `${fixed(back.value, 4)}" per tooth` },
        ],
        steps: solved.steps,
      };
    },
  },
  {
    id: 'convert',
    name: 'Inch, fraction and millimetre',
    blurb: 'One size, said every way, with the nearest drill.',
    inputs: [{ key: 'inches', label: 'Size', unit: '"', initial: 0.625 }],
    compute: (v) => {
      const inches = num(v, 'inches');
      const toMm = inchToMm(inches);
      const frac = decimalToFraction(inches);
      const drill = nearestDrill(inches);
      const fractionDrill = nearestDrill(inches, 'fraction');
      const { numerator, denominator } = frac.value.fraction;

      return {
        outputs: [
          { label: 'Millimetres', value: `${fixed(toMm.value, 3)} mm`, primary: true },
          {
            label: frac.value.exact ? 'Exactly' : 'Nearest 64th',
            value: denominator === 1 ? `${numerator}"` : `${numerator}/${denominator}"`,
          },
          {
            label: 'Nearest drill',
            value: `${drill.drill.label} (${fixed(drill.drill.inches, 4)}")`,
          },
          {
            label: 'Nearest fractional drill',
            value: `${fractionDrill.drill.label} (${fixed(fractionDrill.drill.inches, 4)}")`,
          },
        ],
        steps: [...toMm.steps, ...frac.steps],
      };
    },
  },
  {
    id: 'bolt',
    name: 'Bolt circle coordinates',
    blurb: 'Every hole, X and Y from the centre.',
    inputs: [
      { key: 'diameter', label: 'Bolt circle diameter', unit: '"', initial: 4 },
      { key: 'holes', label: 'Holes', initial: 6, integer: true },
      { key: 'start', label: 'First hole at', unit: '°', initial: 0 },
    ],
    compute: (v) => {
      const solved = boltHoles(num(v, 'diameter'), num(v, 'holes'), num(v, 'start'));
      return {
        outputs: [
          {
            label: 'Between holes',
            value: deg(360 / num(v, 'holes'), 360 % num(v, 'holes') === 0 ? 0 : 3),
            primary: true,
          },
          { label: 'Radius', value: `${fixed(num(v, 'diameter') / 2, 4)}"` },
        ],
        table: {
          headers: ['Hole', 'Angle', 'X', 'Y'],
          rows: solved.value.map((h) => [
            String(h.index + 1),
            deg(h.angleDegrees, 2),
            fixed(h.x, 4),
            fixed(h.y, 4),
          ]),
        },
        steps: solved.steps,
      };
    },
  },
  {
    id: 'taper',
    name: 'Taper',
    blurb: 'Two diameters and a length into per-inch, per-foot and the compound.',
    inputs: [
      { key: 'large', label: 'Large diameter', unit: '"', initial: 0.75 },
      { key: 'small', label: 'Small diameter', unit: '"', initial: 0.5 },
      { key: 'length', label: 'Length', unit: '"', initial: 2 },
    ],
    compute: (v) => {
      const solved = taperFromDiameters(num(v, 'large'), num(v, 'small'), num(v, 'length'));
      const t = solved.value;
      return {
        outputs: [
          { label: 'Taper per foot', value: `${fixed(t.tpf, 4)}"`, primary: true },
          { label: 'Taper per inch', value: `${fixed(t.tpi, 4)}"` },
          { label: 'Included angle', value: deg(t.includedAngle, 3) },
          { label: 'Set the compound to', value: deg(t.compoundAngle, 3) },
        ],
        steps: solved.steps,
      };
    },
  },
  {
    id: 'limits',
    name: 'Limits and fit',
    blurb: 'Turn a callout into the two numbers you actually inspect against.',
    inputs: [
      { key: 'nominal', label: 'Nominal', unit: '"', initial: 1.25 },
      { key: 'upper', label: 'Plus', unit: '"', initial: 0.002 },
      { key: 'lower', label: 'Minus', unit: '"', initial: 0 },
    ],
    compute: (v) => {
      const solved = limits(num(v, 'nominal'), num(v, 'upper'), num(v, 'lower'));
      const band = solved.value;
      return {
        outputs: [
          { label: 'High limit', value: `${fixed(band.max, 4)}"`, primary: true },
          { label: 'Low limit', value: `${fixed(band.min, 4)}"`, primary: true },
          { label: 'Total tolerance', value: `${fixed(band.total, 4)}"` },
          { label: 'Aim for', value: `${fixed((band.min + band.max) / 2, 4)}"` },
        ],
        steps: solved.steps,
      };
    },
  },
  {
    id: 'fit',
    name: 'Hole and shaft fit',
    blurb: 'Does it drop in, or does it need the press?',
    inputs: [
      { key: 'hole', label: 'Hole size', unit: '"', initial: 0.5 },
      { key: 'shaft', label: 'Shaft size', unit: '"', initial: 0.4995 },
    ],
    compute: (v) => {
      const solved = fit(num(v, 'hole'), num(v, 'shaft'));
      const names = {
        clearance: 'Clearance — it drops in',
        interference: 'Interference — press fit',
        'line-to-line': 'Line to line',
      } as const;
      return {
        outputs: [
          { label: 'Fit', value: names[solved.value.type], primary: true },
          { label: 'Allowance', value: `${fixed(Math.abs(solved.value.allowance), 4)}"` },
        ],
        steps: solved.steps,
      };
    },
  },
  {
    id: 'sinebar',
    name: 'Sine bar stack',
    blurb: 'An angle into a stack of gauge blocks.',
    inputs: [
      { key: 'bar', label: 'Sine bar length', unit: '"', initial: 5 },
      { key: 'angle', label: 'Angle', unit: '°', initial: 30 },
    ],
    compute: (v) => {
      const solved = sineBarStack(num(v, 'bar'), num(v, 'angle'));
      const complement = sineBarStack(num(v, 'bar'), 90 - num(v, 'angle'));
      return {
        outputs: [
          { label: 'Gauge block stack', value: `${fixed(solved.value, 4)}"`, primary: true },
          {
            label: `If the angle is off the vertical (${deg(90 - num(v, 'angle'), 2)})`,
            value: `${fixed(complement.value, 4)}"`,
          },
        ],
        steps: solved.steps,
      };
    },
  },
  {
    id: 'tapdrill',
    name: 'Tap drill',
    blurb: 'Thread size and engagement into the drill you reach for.',
    inputs: [
      { key: 'major', label: 'Major diameter', unit: '"', initial: 0.25 },
      { key: 'tpi', label: 'Threads per inch', initial: 20 },
      { key: 'percent', label: 'Percent of thread', unit: '%', initial: 75 },
    ],
    compute: (v) => {
      const solved = tapDrillInch(num(v, 'major'), num(v, 'tpi'), num(v, 'percent'));
      const t = solved.value;
      return {
        outputs: [
          {
            label: 'Drill',
            value: `${t.drill.drill.label} — ${fixed(t.drill.drill.inches, 4)}"`,
            primary: true,
          },
          { label: 'Theoretical size', value: `${fixed(t.size, 4)}"` },
          { label: 'That drill actually gives', value: `${fixed(t.actualPercent, 0)}% of thread` },
          { label: 'Pitch', value: `${fixed(1 / num(v, 'tpi'), 4)}"` },
        ],
        steps: solved.steps,
      };
    },
  },
  {
    id: 'plusminus',
    name: '± callout',
    blurb: 'The one that catches people: ± doubles.',
    inputs: [
      { key: 'nominal', label: 'Nominal', unit: '"', initial: 2 },
      { key: 'tolerance', label: 'Plus and minus', unit: '"', initial: 0.005 },
    ],
    compute: (v) => {
      const solved = bilateralLimits(num(v, 'nominal'), num(v, 'tolerance'));
      const band = solved.value;
      return {
        outputs: [
          {
            label: 'Range',
            value: `${fixed(band.min, 4)}" to ${fixed(band.max, 4)}"`,
            primary: true,
          },
          { label: 'Total tolerance', value: `${fixed(band.total, 4)}"` },
        ],
        steps: solved.steps,
      };
    },
  },
];

export const TOOL_BY_ID: ReadonlyMap<string, ToolDef> = new Map(TOOLS.map((t) => [t.id, t]));

/**
 * Run a calculator, converting a solver's complaint into a message rather than
 * an exception. Half-typed input is the normal state of a calculator, and a
 * blank field must not blank the screen.
 */
export function runTool(
  tool: ToolDef,
  values: Readonly<Record<string, number>>,
): { result: ToolResult } | { error: string } {
  try {
    return { result: tool.compute(values) };
  } catch (e) {
    if (e instanceof ShopMathError) return { error: e.message };
    return { error: 'Those numbers do not make a shape that exists.' };
  }
}

export function initialValues(tool: ToolDef): Record<string, number> {
  return Object.fromEntries(tool.inputs.map((i) => [i.key, i.initial]));
}

export const formatInitial = (input: ToolInput): string =>
  input.integer ? String(input.initial) : trim(input.initial, 4);
